import React, { FC, useContext } from "react";
import { useTranslation, Trans } from "i18n";
import { useQueryClient } from "react-query";
import { StatusMessage } from "component-library";
import Section from "../Section";
import PaymentMethodButton from "./PaymentMethodButton";
import {
  CheckoutFlow,
  ExistingPaymentMethod,
  ISubscription,
  PaymentMethodType,
  PlanCodeMethod,
  PlanCodePeriod,
} from "../../types";
import "./styles.scss";
import { BillingUser } from "translations/src/models/billing";
import { BillingContext } from "../../BillingContext";
import { Spinner } from "../../../../Atoms/Spinner/Spinner";
import ExistingPaymentMethodBlock from "./ExistingPaymentMethodBlock/ExistingPaymentMethodBlock";
import { ReactComponent as VisaIcon } from "../../../../../images/svg/icons/visa.svg";
import { ReactComponent as MasterCardIcon } from "../../../../../images/svg/icons/mastercard.svg";
import { ReactComponent as SepaIcon } from "../../../../../images/svg/icons/sepa.svg";
import { ReactComponent as DirectDebitIcon } from "../../../../../images/svg/icons/direct-debit.svg";
import { FETCH_SUBSCRIPTIONS } from "../../services";

interface Props {
  existingPaymentMethodData: ExistingPaymentMethod | undefined;
  existingPaymentMethodIsLoading: boolean;
  existingPaymentMethodIsError: boolean;
  selectedMethod?: PlanCodeMethod;
  setPaymentMethod: (paymentMethodType: PlanCodeMethod) => void;
}

const PaymentMethodBlock: FC<React.PropsWithChildren<Props>> = ({
  selectedMethod,
  setPaymentMethod,
  existingPaymentMethodData,
  existingPaymentMethodIsLoading = false,
  existingPaymentMethodIsError = false,
}) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const {
    setUseExistingPaymentMethod,
    useExistingPaymentMethod,
    billingUserType,
    checkoutFlow,
    planPeriodData: { selectedPeriod },
  } = useContext(BillingContext);

  const subscriptionsData =
    queryClient.getQueryData<ISubscription[]>(FETCH_SUBSCRIPTIONS) || [];

  const method =
    existingPaymentMethodData?.paymentMethodType ===
    PaymentMethodType.CreditCard
      ? PlanCodeMethod.CreditCard
      : PlanCodeMethod.DirectDebit;

  const handleSelectExistingPaymentMethod = () => {
    setUseExistingPaymentMethod(true);
    setPaymentMethod(method);
  };

  const handleSelectPaymentMethod = (planCodeMethod: PlanCodeMethod) => {
    setPaymentMethod(planCodeMethod);
    setUseExistingPaymentMethod(false);
  };

  const isGraduate =
    billingUserType === BillingUser.JoiningGraduate ||
    checkoutFlow === CheckoutFlow.GraduateTransfer;
  const isMonthly = selectedPeriod === PlanCodePeriod.Monthly;
  // Change plan flow does not allow existing payment method selection, will be removed when BE develop this flow
  const isChangePlanFlow = checkoutFlow === CheckoutFlow.ChangePlan;

  const renderExistingPaymentMethod = () => {
    if (existingPaymentMethodIsError) {
      setUseExistingPaymentMethod(false);

      return null;
    }

    // For some reason this useQuery sets the response from a 404 as data, instead of throwing an error. Instead checking for a value that must exist on successful call.
    if (existingPaymentMethodData?.paymentMethodType) {
      if (
        existingPaymentMethodData?.paymentMethodType ===
          PaymentMethodType.CreditCard &&
        isMonthly &&
        isGraduate
      ) {
        return null;
      }

      if (existingPaymentMethodIsLoading) return <Spinner inline />;
      else {
        return (
          <PaymentMethodButton
            isSelected={useExistingPaymentMethod}
            onSelect={handleSelectExistingPaymentMethod}
            variant="secondary"
          >
            <ExistingPaymentMethodBlock data={existingPaymentMethodData} />
          </PaymentMethodButton>
        );
      }
    } else return null;
  };

  const shouldShowCC =
    !isChangePlanFlow &&
    subscriptionsData.some((sub: ISubscription) =>
      sub.code.includes(PlanCodeMethod.CreditCard)
    );

  const shouldShowDD =
    !isChangePlanFlow &&
    subscriptionsData.some((sub: ISubscription) =>
      sub.code.includes(PlanCodeMethod.DirectDebit)
    );

  return (
    <Section isOpen customClasses="max-w-[680px]">
      <h2 className="text-[1.1rem] mb-4">
        {t("common:billing.select.payment.method")}
      </h2>
      <div className="flex flex-col gap-[4px]">
        {renderExistingPaymentMethod()}
        {!existingPaymentMethodData?.isValid && (
          <StatusMessage className="mt-5">
            <p className="font-semibold">
              {t(
                "common:billing.updateSubscription.warning.expiredMethod.title"
              )}
            </p>
            <p>
              <Trans
                i18nKey="billing.updateSubscription.warning.expiredMethod.body"
                components={{
                  a: (
                    <a
                      href="mailto:questions@spotlight.com"
                      aria-label="support"
                    />
                  ),
                }}
              />
            </p>
          </StatusMessage>
        )}
        {shouldShowDD && (
          <PaymentMethodButton
            isSelected={
              selectedMethod === PlanCodeMethod.DirectDebit &&
              !useExistingPaymentMethod
            }
            onSelect={() =>
              handleSelectPaymentMethod(PlanCodeMethod.DirectDebit)
            }
            id="payment-method-button-direct-debit"
          >
            {t("common:button.label.add.dd")}
            <div className="flex items-center gap-2 ml-auto">
              <div className="py-2 px-1.5 border border-grey-light-1 rounded bg-white">
                <SepaIcon
                  height={10}
                  width={28}
                  className="payment-method__logo"
                />
              </div>
              <div className="direct-debit">
                <DirectDebitIcon
                  height={21}
                  width={56}
                  className="payment-method__logo fill-black"
                />
              </div>
            </div>
          </PaymentMethodButton>
        )}
        {shouldShowCC && (
          <PaymentMethodButton
            isSelected={
              selectedMethod === PlanCodeMethod.CreditCard &&
              !useExistingPaymentMethod
            }
            onSelect={() =>
              handleSelectPaymentMethod(PlanCodeMethod.CreditCard)
            }
            id="payment-method-button-credit-card"
          >
            {t("common:button.label.add.cc")}
            <div className="flex gap-2 ml-auto">
              <div className="py-2 px-1.5 border border-grey-light-1 rounded bg-white">
                <VisaIcon
                  height={10}
                  width={28}
                  className="payment-method__logo"
                />
              </div>
              <div className="px-2 bg-white border rounded border-grey-light-1">
                <MasterCardIcon
                  height={25}
                  width={22}
                  className="payment-method__logo"
                />
              </div>
            </div>
          </PaymentMethodButton>
        )}
      </div>
    </Section>
  );
};

export default PaymentMethodBlock;
